import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, RotateCcw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MAX_SECONDS = 120;
const pickMime = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac'].find((m) => MediaRecorder.isTypeSupported?.(m)) || '';
};

/** Tap to record a short voice note; returns a data URL via onChange (null when cleared). */
export const VoiceRecorder = ({ onChange, testId = 'voice' }) => {
  const [state, setState] = useState('idle'); // idle | recording | done | denied
  const [secs, setSecs] = useState(0);
  const [url, setUrl] = useState(null);
  const recRef = useRef(null);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const stopTracks = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };
  useEffect(() => () => { clearInterval(timerRef.current); stopTracks(); }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        stopTracks();
        const blob = new Blob(chunks, { type: rec.mimeType || mime || 'audio/webm' });
        setUrl(URL.createObjectURL(blob));
        const fr = new FileReader();
        fr.onloadend = () => onChange(fr.result);
        fr.readAsDataURL(blob);
        setState('done');
      };
      recRef.current = rec;
      rec.start();
      setSecs(0); setState('recording');
      timerRef.current = setInterval(() => setSecs((s) => {
        if (s + 1 >= MAX_SECONDS) stop();
        return s + 1;
      }), 1000);
    } catch { setState('denied'); }
  };
  const stop = () => { clearInterval(timerRef.current); if (recRef.current?.state === 'recording') recRef.current.stop(); };
  const clear = () => { setUrl(null); setSecs(0); setState('idle'); onChange(null); };
  const mmss = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;

  return (
    <div className="rounded-md border border-dashed border-gray-300 p-3 space-y-2" data-testid={`${testId}-recorder`}>
      {state === 'idle' && <Button type="button" variant="outline" onClick={start} className="w-full" data-testid={`${testId}-record-btn`}><Mic className="w-4 h-4 mr-2 text-red-600" />Record voice reason</Button>}
      {state === 'denied' && <p className="text-xs text-red-600" data-testid={`${testId}-denied`}>Microphone blocked — allow the mic for this site, or type the reason below.</p>}
      {state === 'recording' && (
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 text-sm text-red-600 font-medium" data-testid={`${testId}-recording`}><span className="w-2.5 h-2.5 rounded-full bg-red-600 animate-pulse" />Recording {mmss}</span>
          <Button type="button" size="sm" onClick={stop} className="bg-red-600 hover:bg-red-700" data-testid={`${testId}-stop-btn`}><Square className="w-3.5 h-3.5 mr-1" />Stop</Button>
        </div>
      )}
      {state === 'done' && url && (
        <div className="space-y-2">
          <audio controls src={url} className="w-full h-9" data-testid={`${testId}-playback`} />
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => { clear(); start(); }} data-testid={`${testId}-rerecord-btn`}><RotateCcw className="w-3.5 h-3.5 mr-1" />Re-record</Button>
            <Button type="button" size="sm" variant="ghost" onClick={clear} data-testid={`${testId}-clear-btn`}><Trash2 className="w-3.5 h-3.5 mr-1" />Remove</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;
